import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { fmt } from "@/lib/format";
import { EmptyRow, Money } from "@/components/common";
import { Button } from "@/components/ui/button";

export function FxReport({ range }) {
  const { t } = useLang();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/fx", { params: range }).then((r) => setD(r.data)); }, [range]);
  if (!d) return null;
  const csv = () => {
    const head = ["payment_date", "number", "client", "currency", "fx_total", "invoice_rate", "payment_rate", "invoiced_aed", "received_aed", "diff_aed"];
    const lines = d.rows.map((r) => [r.payment_date, r.number, `"${r.client_name}"`, r.currency, r.fx_total, r.fx_rate, r.payment_fx_rate, r.invoiced_aed, r.received_aed, r.diff].join(","));
    const blob = new Blob(["\ufeff" + [head.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const u = URL.createObjectURL(blob);
    Object.assign(document.createElement("a"), { href: u, download: `fx-report-${range.start}-${range.end}.csv` }).click();
    URL.revokeObjectURL(u);
  };
  return (
    <div data-testid="fx-report">
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 pt-5">
        <p className="text-sm text-slate-500">{t("fxReportText")}</p>
        <Button data-testid="fx-report-csv" size="sm" variant="outline" onClick={csv} disabled={!d.rows.length}><Download className="me-1 h-4 w-4" />CSV</Button>
      </div>
      <div className="grid grid-cols-1 gap-3 p-6 sm:grid-cols-3">
        {[["fxGains", d.gains, "text-[#007A3D]", "fx-gains"], ["fxLosses", d.losses, "text-[#CE1126]", "fx-losses"], ["fxNet", d.net, d.net >= 0 ? "text-[#007A3D]" : "text-[#CE1126]", "fx-net"]].map(([k, v, cls, tid]) => (
          <div key={k} className="rounded-lg border border-slate-200 p-4"><div className="text-xs uppercase tracking-wider text-slate-500">{t(k)}</div><Money testid={tid} value={v} className={`text-xl font-bold ${cls}`} /></div>
        ))}
      </div>
      <table className="data-table">
        <thead><tr><th>{t("paymentDate")}</th><th>{t("invoiceNumber")}</th><th>{t("client")}</th><th>{t("currency")}</th><th className="text-end">{t("amount")}</th><th className="text-end">{t("fxRate")} ({t("invoice")})</th><th className="text-end">{t("fxRate")} ({t("payment")})</th><th className="text-end">{t("fxInvoiced")}</th><th className="text-end">{t("fxReceived")}</th><th className="text-end">{t("fxDiff")}</th></tr></thead>
        <tbody>
          {d.rows.length === 0 && <EmptyRow cols={10} />}
          {d.rows.map((r) => (
            <tr key={r.id} data-testid={`fx-row-${r.number}`}>
              <td className="font-mono text-slate-500">{r.payment_date}</td><td className="font-mono font-semibold">{r.number}</td><td>{r.client_name}</td><td>{r.currency}</td>
              <td className="text-end font-mono">{fmt(r.fx_total)} {r.currency}</td><td className="text-end font-mono">{r.fx_rate}</td><td className="text-end font-mono">{r.payment_fx_rate}</td>
              <td className="text-end"><Money value={r.invoiced_aed} cur={false} /></td><td className="text-end"><Money value={r.received_aed} cur={false} /></td>
              <td className={`text-end font-bold ${r.diff >= 0 ? "text-[#007A3D]" : "text-[#CE1126]"}`}>{r.diff >= 0 ? "+" : ""}{fmt(r.diff)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
